import React from 'react';
import { Cascader, Spin, Select, Divider, Row, Col, Typography, Space, Button, Progress, Card, Modal, notification } from 'antd';

import useGetData from "../../hooks/useGetData";
import { getNetworksData, applyWirelessConfig, streamConfigResult } from '../../api/networks';

import type { SelectProps } from 'antd';
import type { DefaultOptionType } from 'antd/es/cascader';

import useNoti from '../../hooks/useNoti';
import { ExclamationCircleFilled } from '@ant-design/icons';
//import { axiosApiInstance } from '../../services/axiosApiInstance';

interface Option {
    value: string;
    label: string;
    children?: Option[];
    disabled?: boolean;
}


const dropdownRender = (menus: React.ReactNode) => {        
    return (
        <div style={{ width: 400 }}>
            {menus}
            <Divider style={{ margin: 0 }} />
            <Row gutter={24} style={{margin: '8px 0'}}>
                <Col>
                Choose a site on left column and a network on right column
                </Col>                    
            </Row>                                
        </div>
    )
}


const NetworksConfigurationPage: React.FC = () => {

    const noti = useNoti();

    const { data, isLoading, isFetching } = useGetData('networks', getNetworksData);
    

    const [netWorkOptions, setNetworkOptions] = React.useState<Array<Option>>([]);
    const [selectOptions, setSelectOptions] = React.useState<SelectProps['options']>([]);
    const [selectedItem, setSelectedItem] = React.useState<string[]>([]);

    const [selectedConfigSiteId, setSelectedConfigSiteId] = React.useState<string | number>();
    const [selectedConfigNetworkId, setSelectedNetworkId] = React.useState<string | number>();

    const [applyLoading, setApplyLoading] = React.useState(false);
    const [applyBtnStatus, setApplyBtnStatus] = React.useState(false);
    const [configApplyPercent, setConfigApplyPercent] = React.useState(0);

    const filter = (inputValue: string, path: DefaultOptionType[]) =>
        path.some(
            (option) => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1,
        );

    React.useEffect(() => {
        if (
            (!isFetching || !isFetching) &&
            data && data.length > 0
        ) {            

            const sitesSelect: SelectProps['options'] = [{
                value: 'all',
                label: 'All Sites',   
                disabled: false
            }];
            
            // Transform the networks into cascader options

            const cascaderOptions: Option[] = []
            data.forEach( (network: any) => {  
                const [site_name, nw]: [string, any] = Object.entries(network)[0];
                const networksOfSite = nw['wirelessNetworks'].map( (wireless: any) => {
                    return {
                        value: wireless['id'],
                        label: `(ap) ${wireless['networkName']}`
                    }
                } )

                sitesSelect.push({
                    value: nw['site_id'],
                    label: site_name,
                    disabled: false
                });

                cascaderOptions.push({
                    value: nw['site_id'],
                    label: `(site) ${site_name}`,
                    children: networksOfSite
                })
            });
            
            setNetworkOptions(cascaderOptions);
            setSelectOptions(sitesSelect);
        }
    }, [isLoading, isFetching, data]);

    React.useEffect(() => {        
        setApplyBtnStatus(selectedItem.length <= 0 || !selectedConfigSiteId || !selectedConfigNetworkId);        
    }, [selectedItem, selectedConfigSiteId, selectedConfigNetworkId]);
    
    const applyConfigHandle = async () => {                
        // button effect
        setApplyBtnStatus(true);
        setApplyLoading(true);

        // Find the selected network in the data set and return the first value in array if it is found
        const networks = (data as Array<any>).find(site => {            
            const [siteInfo] = Object.values(site) as any;                                      
            return siteInfo['site_id'] === selectedConfigSiteId?.toString()                      
        })

        if (networks) {
            const siteInfo = Object.values(networks)[0] as any;

            const [, wirelessNetworks] = Object.values(siteInfo) as [string, Array<any>];

            const networkSummary = wirelessNetworks.find(network => network['id'] === selectedConfigNetworkId);

            const dataConfig = buildConfigData(networkSummary);
            console.log(dataConfig)

            let sites: string[] = [];
            if ( selectedItem.includes("all") ) {
                if (selectOptions) {
                    selectOptions.forEach(option => {
                        if (option.value && option.value !== "all" && option.value !== selectedConfigSiteId) {
                            sites.push(option.value.toString());
                        }                    
                    });
                }
            }
            else {
                sites = selectedItem
            }

            if (sites === undefined || sites === null) {
                noti?.pushNoti({
                    type: 'error',
                    message: "Sites empty",
                    description: "Could not load sites",
                    duration: 7
                });                
            }
            else {
                const uuid = await applyWirelessConfig(sites, dataConfig)
                if (uuid) {                    
                    localStorage.setItem("uuid", uuid)
                    const url = streamConfigResult(uuid)

                    if ( 'EventSource' in window ) {
                        
                        const source = new EventSource(url, { withCredentials: true });

                        source.addEventListener('open', (e) => {
                            console.log('open stream', e)
                        }, false)

                        source.addEventListener('new_config_message', (e) => {                            
                            setConfigApplyPercent( (parseFloat(e.data) * 100) / sites.length )
                            // if ( String(e.data).trim() === "END_OF_STREAM" ) {
                            //     source.close();
                            // }
                        }, false)
                        
                        source.addEventListener('end_config_message', e => {
                            source.close();
                            localStorage.removeItem("uuid")
                            setConfigApplyPercent( 100 )
                            setApplyBtnStatus(false)
                            setApplyLoading(false);
                            notification.success({
                                message: "Success",
                                description: "Config applied to selected sites",
                            })
                        }, false)
                        
                        source.addEventListener('error', (e) => {
                            source.close();
                            setApplyBtnStatus(true);
                            setConfigApplyPercent(0);
                            console.log('error', e);
                        }, false)
                    }
                
                }
            }

        }                        
    }

    const buildConfigData = React.useCallback((networkSummary: any) => {

        const defaultActiveDays = [
            "monday",
            "tuesday",
            "wednesday",
            "thursday",
            "friday"
        ]        
        const defaultActiveTimeRange = {
            "enabled": false
        }   
        const configData = {
            ...networkSummary,            
            "allowList": {
                "id": null,
                "isAllowListEnabled": false,
                "isExtendAllowListEnabled": false
            },
            "applicationCategoryUsage": null,
            "schedule": {
                ...networkSummary["schedule"],
                "activeDays": networkSummary["schedule"]?.["enabled"] ? networkSummary["schedule"]["activeDays"] : defaultActiveDays,
                "activeTimeRange": networkSummary["schedule"]?.["enabled"] ? networkSummary["schedule"]["activeTimeRange"] : defaultActiveTimeRange
            }
        }
        
        // bssIndex, id, wirelessClientsCount, wiredNetworkId, accessPoints, kind
        delete configData.accessPoints;
        delete configData.bssIndex;
        delete configData.id;
        delete configData.wirelessClientsCount;
        delete configData.wiredNetworkId;
        delete configData.kind;
        return configData;
    }, []);

    const showConfirm = () => {        
        let apName = undefined;
        netWorkOptions.forEach(site => {
            let name = site.children?.filter(ap => ap.value === selectedConfigNetworkId);
            if (name && name.length > 0) {                
                apName = name[0].label;                
                return;
            }
        })

        let sitesCount = 0;
        if (selectOptions) {
            sitesCount = selectOptions?.length - 1;
            if (!selectedItem.includes("all")) {
                sitesCount = selectedItem.length;
            }
        }
        
        
        Modal.confirm({
            title: 'Apply the network configuration',
            icon: <ExclamationCircleFilled />,
            content: (
                <>
                    <p>You selected <strong>{`(${sitesCount})`}</strong> sites.</p>
                    <p>Do You want to apply the configuration of the <strong>{`${apName}`}</strong> for those sites?</p>
                </>
            ),
            onOk: async () => {
                try {
                    await applyConfigHandle();                
                }                
                catch (err) {
                    console.error(err);
                }
            },
            onCancel() {
                // console.log('Cancel');
            },
            okButtonProps: {
                style: {
                    'backgroundColor': '#FF8300'
                }
            }
        });
    };

    return (
        <>
            <Spin size="large" spinning={isLoading || isFetching}>
                <Card bordered={false} title={'Copy network configuration'}>
                    <Space direction='vertical' size={'middle'}>                
                        <Space direction='vertical'>
                            <Typography.Text>Choose a network config</Typography.Text>
                            <Cascader                
                                options={netWorkOptions}
                                onChange={(value, selectedOptions) => {
                                    // console.log(value, selectedOptions);
                                    setSelectedConfigSiteId(value[0]);
                                    setSelectedNetworkId(value[1]);
                                }}                                        
                                placeholder="Please select"
                                showSearch={{ filter }}
                                // onSearch={(value) => console.log(value)}
                                style={{
                                    width: 400
                                }}
                                dropdownRender={dropdownRender}
                            /> 
                        </Space>

                        <Space direction='vertical'>
                            <Typography.Text>Choose site to apply the config</Typography.Text>
                            <Select                     
                                mode='multiple'
                                allowClear
                                style={{ width: 400 }}
                                onChange={(value, options) => {                         
                                    if (value.includes("all")) {
                                        setSelectedItem(["all"])                            
                                    }
                                    else {
                                        setSelectedItem(value)
                                    }        
                                    
                                    options?.forEach((option: DefaultOptionType) => {
                                        if ( option.value === "all" && selectOptions ) {                                                  
                                            setSelectOptions([...selectOptions.map(option => {
                                                if (option.value !== "all") {
                                                    option.disabled = true;
                                                }
                                                return option
                                            })])                                                           
                                        }
                                    })                                                                        
                                }}
                                onDeselect={(value, option) => {                        
                                    if (option.value === "all" && selectOptions ) {
                                        setSelectOptions([...selectOptions.map(option => {
                                            if (option.value !== "all") {
                                                option.disabled = false;
                                            }
                                            return option
                                        })])
                                    }                        
                                }}
                                onClear={() => {
                                    setSelectedItem([])
                                    if (selectOptions) {
                                        setSelectOptions([...selectOptions.map(option => {
                                            option.disabled = false;
                                            return option;
                                        })])
                                    }                        
                                }}
                                options={selectOptions}
                                value={selectedItem}
                            />
                        </Space>
                        
                        {(!applyLoading) ? (
                            <Button 
                                // onClick={applyConfigHandle} 
                                onClick={showConfirm}
                                disabled={applyBtnStatus}                             
                                type='primary'
                            >
                                Apply
                            </Button>
                        ) : (
                            <Space direction='vertical'>
                                <Progress percent={configApplyPercent} size={[500, 20]} />
                            </Space>
                        )}
                                                
                        {/* {applyBtnStatus && selectedItem.length > 0 && selectedConfigNetworkId && selectedConfigSiteId && (
                            
                        )} */}
                    </Space>
                </Card>                      
            </Spin>            
        </>
    );
}

export default NetworksConfigurationPage;